import BaseTypeController from "vamos/cockpit/base/controller/BaseTypeController";
import JSONModel from "sap/ui/model/json/JSONModel";
import View from "sap/ui/core/mvc/View";
import Dialog from "sap/m/Dialog";
import { Select$ChangeEvent } from "sap/m/Select";
import DateFormat from "sap/ui/core/format/DateFormat";
import DynamicDateUtil from "sap/m/DynamicDateUtil";
import Item from "sap/ui/core/Item";
import ValidationUtil from "vamos/cockpit/base/util/ValidationUtil";
import { ReportFileType, ReportType } from "vamos/cockpit/model/api/vamos.private.api.types";
import { RequestSort } from "vamos/cockpit/model/odata/VamosModel";
import ApiFileHandler from "vamos/cockpit/base/handler/ApiFileHandler";

/**
 * @namespace vamos.cockpit.main.menu.controller
 * */
export default class ReportStatisticDialogController extends BaseTypeController {
  private parentView: View;
  private dialog: Dialog;

  onInit(): void {
      super.onInit();
    this.viewModel = new JSONModel({});
    this.setModel(this.viewModel, "view");


    this.dialog = this.getView().byId("reportStatisticDialog") as Dialog;

  }

  onAfterRendering(): void {
    if (!this.parentView) {
      this.parentView = this.findParentControl("sap.ui.core.mvc.XMLView", this.getView()) as View;
    }
  }

  public openDialog(): void {
    // init view data
    let defaultSelectedPeriod = "PERIOD";
    let defaultReportType = ReportType.REQUEST;

    ValidationUtil.clearRequiredFields(this.dialog);
    this.getViewModel().setData({
      selectedPeriod: defaultSelectedPeriod,
      payload: {
        reportType: defaultReportType,
        fileType: ReportFileType.EXCEL,
      },
      showSelection: false,
      currentSortItems: [],
    });
    this.dialog.open();
    this.changePeriod(defaultSelectedPeriod);
    this.changeReportType(defaultReportType);
  }

  public onClosePopup(): void {
    this.dialog.close();
  }

  public handlePeriodChanged(event: Select$ChangeEvent): void {
    const key = event.getParameter("selectedItem").getKey();
    this.changePeriod(key);
  }

  private changePeriod(key: string): void {
    this.setPayloadDate(null, null);
    this.getViewModel().setProperty("/selectedDateInterval", null);

    let startDate;
    let endDate;

    if (key === "PERIOD") {
      this.setShowSelectedBox(false);
    } else if (key === "YEAR" || key === "SPECIFICMONTH") {
      this.loadDateSelectBox(key);
      this.setShowSelectedBox(true);
    } else {
      this.setShowSelection(false);
      [startDate, endDate] = this.getDateFromKey(key);
      this.setPayloadDate(startDate, endDate);
    }
  }

  public handleDateSelectionChanged(event: Select$ChangeEvent): void {
    let startDate;
    let endDate;
    const period: string = this.getViewModel().getProperty("/selectedPeriod");
    const selectedKey: string = event.getParameter("selectedItem").getKey();

    if (period === "SPECIFICMONTH") {
      [startDate, endDate] = this.getDateFromKey(period, [selectedKey]);
      this.setPayloadDate(startDate, endDate);
    } else if (period === "YEAR") {
      const year = parseInt(selectedKey);
      startDate = new Date(year, 0, 1);
      endDate = new Date(year - 1, 11, 31, 23, 59, 59);
    }
    this.setPayloadDate(startDate, endDate);
  }

  public handleReportTypeChange(event: Select$ChangeEvent) {
    const coreItem: Item = event.getParameter("selectedItem") as Item;
    const key: string = coreItem.getKey();
    this.changeReportType(key);
  }

  private changeReportType(key: string): void {
    let reportType: string = (() => {
      switch (key) {
        case "ORDER":
          return "OrderSort";
        case "REQUEST":
          return "RequestSort";
        case "SALE":
          return "SaleSort";
        case "STOCK":
          return "StockSort";
      }
    })();
    const enumModel: JSONModel = this.getModel("enum") as JSONModel;
    const currentItems: any = enumModel.getProperty("/items").filter((item: any) => item.key === reportType).sort((a: any, b: any) => a.translatedText.localeCompare(b.translatedText));
    this.setCurrentSortItems(currentItems);
    let defaultSorterKey = null;
    if(currentItems.length > 0) {
      defaultSorterKey = currentItems[0].value;
    }
    this.getViewModel().setProperty("/payload/sorterType", defaultSorterKey);
  }

  public async onCreateReport(): Promise<void> {

    const payload = this.getViewModel().getProperty("/payload");
    const hasEmptyRequiredFields: boolean = ValidationUtil.hasEmptyRequiredFields(this.dialog);
    if (hasEmptyRequiredFields) {
      return;
    }
    const extension: string = payload.fileType === "EXCEL" ? "xlsx" : "csv";
    this.dialog.setBusy(true);
    const fileName: string = this.createFileName();
    await ApiFileHandler.downloadFileFromApi("/report/create", payload, fileName, extension);


    this.dialog.setBusy(false);

  }

  public loadDateSelectBox(key: "YEAR" | "SPECIFICMONTH"): void {
    const items = [];
    const date = new Date();
    if (key === "YEAR") {
      let year = date.getFullYear();
      for (let i = 0; i < 10; i++) {
        items.push({ key: year, text: String(year) });
        year--;
      }
    } else if (key === "SPECIFICMONTH") {
      const iCurrentMonth = date.getMonth();
      const aMonths = (DateFormat as any).getInstance().aMonthsWide;
      for (let i = 0; i <= iCurrentMonth; i++) {
        items.push({ key: i, text: aMonths[i] });
      }
    } else {
      console.error("Invalid key for loadDateSelectBox: " + key);
    }

    this.getViewModel().setProperty("/currentIntervalItems", items);
  }

  private createFileName(): string {
    const payload = this.getViewModel().getProperty("/payload");
    const reportType: string = this.getText(`REPORT_TYPE_${payload.reportType}`);
    const sorterType: string = this.getText(`${payload.reportType}_SORT_${payload.sorterType}`);
    return `${reportType}_${sorterType}`;
  }

  private setPayloadDate(startDate: Date, endDate: Date): void {
    const model = this.getViewModel();
    model.setProperty("/payload/startDate", startDate);
    model.setProperty("/payload/endDate", endDate);
  }

  private getDateFromKey(key: string, values: any[] = []): [Date, Date] {
    const aValues = (DynamicDateUtil as any).toDates({ operator: key, values: values });
    return [aValues[0].getJSDate(), aValues[1].getJSDate()];
  }

  public getViewModel() {
    return this.viewModel;
  }

  private setShowSelectedBox(showBox: boolean): void {
    this.setShowSelection(true);
    this.getViewModel().setProperty("/showSelectBox", showBox);
  }

  private setShowSelection(showBox: boolean): void {
    this.getViewModel().setProperty("/showSelection", showBox);
  }

  private setCurrentSortItems(items: any) {
    this.getViewModel().setProperty("/currentSortItems", items);
  }


}